import React, { useState } from 'react';
import { CC_PREVIEW_CAMPAIGN, CC_SUGGESTIONS } from '../../growth-campaign-create/mockData';
import type { CcPreviewCampaign } from '../../growth-campaign-create/types';
import {
  CC_PREVIEW_STEPS,
  GROWTH_CAMPAIGN_PREVIEW_META,
} from '../../growth-campaign-create/config';
import { Icon } from '../Icon/Icon';
import './growth-campaign-create.css';

export interface CreateCampaignPreviewViewProps {
  /** Route param identifying which suggestion was approved. Drives the campaign title. */
  suggestionId?: number;
  /** Real backend draft — when set, replaces internal mock campaign. */
  draft?: CcPreviewCampaign;
  loading?: boolean;
  /** Called when the user wants to go back to the create/edit step. Parent wires routing. */
  onBack?: () => void;
  onLaunch?: () => void;
}

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function CcCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function CcSteps() {
  const steps = CC_PREVIEW_STEPS;
  return (
    <div className="mb-7 flex items-center">
      {steps.map((step, index) => {
        const done = index < 2;
        const isCurrent = index === 2;
        return (
          <div key={step.label} className="flex items-center">
            <div className="flex items-center gap-2">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold"
                style={{
                  background: done ? 'var(--success)' : 'var(--success-tint)',
                  color: done ? 'var(--cream-1)' : 'var(--success)',
                  border: isCurrent
                    ? '2px solid color-mix(in srgb, var(--success) 55%, transparent)'
                    : 'none',
                }}
              >
                {done ? <Icon kind="check" size={14} stroke={3} color="var(--cream-1)" /> : 3}
              </span>
              <span
                className="text-[13px]"
                style={{
                  color: isCurrent ? 'var(--success)' : 'var(--ink-2)',
                  fontWeight: isCurrent ? 600 : 400,
                }}
              >
                {step.label}
              </span>
            </div>
            {index < 2 ? (
              <span className="mx-3 h-px w-10 bg-[color-mix(in_srgb,var(--success)_45%,transparent)]" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function CreateCampaignPreviewView({
  suggestionId,
  draft,
  loading = false,
  onBack,
  onLaunch,
}: CreateCampaignPreviewViewProps = {}) {
  const meta = GROWTH_CAMPAIGN_PREVIEW_META;
  const connected = draft !== undefined;
  const campaign = draft ?? CC_PREVIEW_CAMPAIGN;
  const [adIndex, setAdIndex] = useState(0);

  const sourceSuggestion =
    suggestionId != null ? CC_SUGGESTIONS.find((s) => s.id === suggestionId) : undefined;
  const serviceName = sourceSuggestion?.service ?? campaign.service;
  const serviceIcon = sourceSuggestion?.icon ?? campaign.icon;

  const metrics: { label: string; value: string; tone: string }[] = [
    { label: 'Monthly Budget', value: `$${campaign.budget}`, tone: 'var(--ink-0)' },
    { label: 'Expected Patients', value: campaign.patients, tone: 'var(--success)' },
    { label: 'Cost Per Patient', value: campaign.costPerPatient, tone: 'var(--ink-1)' },
    { label: 'Avg Visit Value', value: `$${campaign.avgCaseValue}`, tone: 'var(--ink-0)' },
    { label: 'Expected Return', value: campaign.roi, tone: 'var(--success)' },
  ];

  // Live drafts permanently return empty ads[] — never blank the whole page.
  const ad = campaign.ads[adIndex] ?? campaign.ads[0];

  return (
    <div className="growth-campaign-create-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-2 flex items-center gap-2">
        <Icon kind="sparkle" size={13} stroke={2} className="text-[var(--ai-3)]" />
        <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
          {meta.eyebrow}
          {loading ? ' · Updating…' : ''}
        </span>
      </div>

      <CcSteps />

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1.5 flex items-center gap-2.5">
            <span className="text-[32px] leading-none">{serviceIcon}</span>
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(24px,3vw,30px)] font-semibold tracking-[-0.035em] text-[var(--ink-0)]">
              {serviceName || 'Campaign'} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
          </div>
          <p className="m-0 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            ELVA built this campaign based on your request. Review everything below, then approve to
            go live.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <span className="inline-flex rounded-[var(--r-pill)] bg-[var(--success-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]">
            {campaign.confidence}% confidence
          </span>
          <span className="inline-flex rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-3)]">
            {campaign.radiusMiles} mi radius
          </span>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-3.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--ink-4)]">
          Your Request
        </span>
        <span className="h-6 w-px bg-[var(--cream-2)]" />
        <span className="flex-1 text-[13px] text-[var(--ink-2)]">
          <strong className="text-[var(--ink-1)]">{campaign.service || '—'}</strong> · $
          {campaign.budget}/mo
          {campaign.instructions && campaign.instructions !== '—'
            ? ` · “${campaign.instructions}”`
            : ''}
        </span>
        <button
          type="button"
          onClick={onBack}
          className="cursor-pointer rounded-[6px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12px] font-semibold text-[var(--ink-2)]"
        >
          Edit
        </button>
      </div>

      <CcCard className="mb-4 p-6">
        <div className="cc-preview-grid grid gap-6" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div>
            <div className="mb-2 text-[13px] font-semibold text-[var(--success)]">
              Who this targets
            </div>
            <p className="text-[14px] leading-[1.7] text-[var(--ink-2)]">{campaign.target}</p>
          </div>
          <div className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] p-[18px]">
            <div className="mb-2 text-[13px] font-semibold text-[var(--gold)]">
              Why now is the right time
            </div>
            <p className="text-[14px] leading-[1.7] text-[var(--ink-2)]">{campaign.whyNow}</p>
          </div>
        </div>
      </CcCard>

      <div className="cc-metrics-grid mb-1 grid gap-2.5" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {metrics.map((m) => (
          <CcCard key={m.label} className="px-4 py-3.5">
            <div className={eyebrowClass}>{m.label}</div>
            <div
              className="mt-1 font-[var(--font-display)] text-[20px] font-bold tracking-[-0.02em]"
              style={{ color: m.tone }}
            >
              {m.value}
            </div>
          </CcCard>
        ))}
      </div>

      <div className="cc-roi-grid mb-6 grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <div className="flex items-center gap-2.5 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] px-[18px] py-3">
          <Icon kind="dollar" size={16} stroke={2} className="shrink-0 text-[var(--success)]" />
          <span className="text-[13px] font-medium text-[var(--success)]">
            For every <strong className="text-[var(--ink-1)]">$1</strong> you spend, expect{' '}
            <strong className="text-[var(--ink-1)]">
              ${campaign.roiEstimateMin}–${campaign.roiEstimateMax}
            </strong>{' '}
            back in first-visit revenue.
          </span>
        </div>
        <div className="flex items-center gap-2.5 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] px-[18px] py-3">
          <Icon kind="team" size={16} stroke={2} className="shrink-0 text-[var(--success)]" />
          <span className="text-[13px] font-medium text-[var(--success)]">{campaign.lifetimeNote}</span>
        </div>
      </div>

      <div className="cc-preview-grid mb-6 grid gap-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <CcCard className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-[15px] font-semibold text-[var(--ink-1)]">Google Ad Preview</span>
            {campaign.ads.length > 0 ? (
              <div className="flex gap-1 rounded-[8px] bg-[var(--surface-mute)] p-1">
                {campaign.ads.map((variant, index) => {
                  const active = index === adIndex;
                  return (
                    <button
                      key={variant.label || `ad-${index}`}
                      type="button"
                      onClick={() => setAdIndex(index)}
                      className="cursor-pointer rounded-[6px] border-0 px-3 py-1 text-[12px] font-semibold"
                      style={{
                        background: active ? 'var(--success-tint)' : 'transparent',
                        color: active ? 'var(--success)' : 'var(--ink-4)',
                      }}
                    >
                      {variant.label || `Ad ${index + 1}`}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          {ad ? (
            <>
              <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-[22px] py-5">
                <div className="mb-1.5 text-[11px] text-[var(--ink-4)]">Sponsored · {ad.url}</div>
                <div className="mb-1.5 font-[var(--font-display)] text-[18px] font-semibold leading-[1.3] text-[var(--info)]">
                  {ad.headline}
                </div>
                <p className="mb-3.5 text-[14px] leading-[1.7] text-[var(--ink-2)]">{ad.description}</p>
                <div className="flex flex-wrap gap-2">
                  {ad.extensions.map((ext) => (
                    <span
                      key={ext}
                      className="rounded-[6px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12px] text-[var(--ink-3)]"
                    >
                      {ext}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-3.5 text-[12px] italic text-[var(--ink-4)]">
                ELVA will A/B test both variants and shift traffic to the winner automatically.
              </div>
            </>
          ) : (
            <div className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-[22px] py-8 text-center text-[13px] text-[var(--ink-3)]">
              {connected
                ? 'Ad variants are not generated for this draft yet. You can still approve & launch.'
                : 'No ad preview available.'}
            </div>
          )}
        </CcCard>

        <CcCard className="p-6">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[15px] font-semibold text-[var(--ink-1)]">Landing Page</span>
            <span className="font-[var(--font-mono)] text-[12px] text-[var(--success)]">
              {campaign.landingUrl}
            </span>
          </div>

          <div className="mb-3.5 flex items-start gap-2.5 rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--info)_30%,transparent)] bg-[var(--info-tint)] px-3 py-2.5">
            <Icon kind="bolt" size={16} stroke={2} className="mt-px shrink-0 text-[var(--info)]" />
            <div className="text-[12px] leading-[1.5] text-[var(--ink-2)]">
              <strong className="text-[var(--info)]">ELVA will generate 3 additional variants</strong>{' '}
              of this page during launch and A/B test all 4 against real traffic. Budget flows to the
              winner automatically.
            </div>
          </div>

          {campaign.landing.length > 0 ? (
            <div className="flex flex-col gap-2">
              {campaign.landing.map((section) => (
                <div
                  key={section.label}
                  className="flex items-start gap-3 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3.5 py-2.5"
                >
                  <span className="mt-px shrink-0 text-[18px] leading-none">{section.icon}</span>
                  <div>
                    <div className="text-[13px] font-semibold text-[var(--ink-1)]">{section.label}</div>
                    <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">{section.description}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3.5 py-6 text-center text-[13px] text-[var(--ink-3)]">
              Landing page sections will be generated on launch.
            </div>
          )}
          <button
            type="button"
            className="mt-3.5 w-full cursor-pointer rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)] px-4 py-2.5 text-[13px] font-semibold text-[var(--success)]"
          >
            Preview Full Landing Page →
          </button>
        </CcCard>
      </div>

      <CcCard className="mb-6 p-6">
        <div className="mb-4 text-[15px] font-semibold text-[var(--ink-1)]">
          Keywords ELVA Will Target
        </div>
        {campaign.keywords.length > 0 ? (
          <div className="cc-keyword-grid grid gap-2" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            {campaign.keywords.map((keyword) => (
              <div
                key={keyword.keyword}
                className="flex items-center justify-between rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3.5 py-2.5"
              >
                <div>
                  <div className="text-[13px] font-medium text-[var(--ink-1)]">{keyword.keyword}</div>
                  <div className="mt-0.5 text-[11px] text-[var(--ink-4)]">
                    {keyword.volume.toLocaleString()} searches/mo
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-[var(--font-mono)] text-[12px] font-medium text-[var(--ink-2)]">
                    ${keyword.cpc}
                  </div>
                  <span
                    className="mt-1 inline-flex rounded-[var(--r-pill)] px-2 py-0.5 text-[10px] font-semibold"
                    style={{
                      background:
                        keyword.competition === 'Very Low'
                          ? 'var(--success-tint)'
                          : 'var(--surface-mute)',
                      color:
                        keyword.competition === 'Very Low' ? 'var(--success)' : 'var(--ink-3)',
                    }}
                  >
                    {keyword.competition}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3.5 py-6 text-center text-[13px] text-[var(--ink-3)]">
            Keywords are not included in this draft yet — they will be assigned on launch.
          </div>
        )}
      </CcCard>

      <CcCard className="mb-6 p-6">
        <div className="mb-4 text-[15px] font-semibold text-[var(--ink-1)]">
          How ELVA Tracks Results
        </div>
        {campaign.tracking.length > 0 ? (
          <div className="cc-tracking-grid grid gap-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
            {campaign.tracking.map((item) => (
              <div
                key={item.label}
                className="rounded-[var(--r-md)] bg-[var(--surface-mute)] px-4 py-3.5"
                style={{ borderLeft: '3px solid var(--success)' }}
              >
                <div className="mb-1 text-[13px] font-semibold text-[var(--success)]">{item.label}</div>
                <div className="text-[12px] leading-[1.5] text-[var(--ink-3)]">{item.description}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3.5 py-6 text-center text-[13px] text-[var(--ink-3)]">
            Tracking setup will be configured when the campaign launches.
          </div>
        )}
        <div className="mt-3.5 text-[12px] italic text-[var(--ink-4)]">
          You&apos;ll see real booked patients and revenue — not just clicks.
        </div>
      </CcCard>

      <CcCard className="flex flex-wrap items-center justify-between gap-3 px-7 py-6">
        <div>
          <div className="font-[var(--font-display)] text-[18px] font-semibold text-[var(--ink-0)]">
            Everything look good?
          </div>
          <div className="mt-1 text-[13px] text-[var(--ink-3)]">
            ELVA will create the ads, publish the landing page, assign a tracking number, and start
            optimizing immediately.
          </div>
        </div>
        <div className="flex shrink-0 gap-2.5">
          <button
            type="button"
            onClick={onBack}
            className="cursor-pointer rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-2.5 text-[14px] font-semibold text-[var(--ink-2)]"
          >
            ← Go Back & Edit
          </button>
          <button
            type="button"
            onClick={onLaunch}
            disabled={loading}
            className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-7 py-3 text-[15px] font-semibold text-[var(--cream-1)] shadow-[var(--sh-1)] disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: 'var(--success)' }}
          >
            <Icon kind="bolt" size={18} stroke={2.4} />
            Approve & Launch Campaign
          </button>
        </div>
      </CcCard>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--cream-2)] pt-4">
        <span className="text-[12px] text-[var(--ink-4)]">
          Campaign generated by ELVA based on your instructions and local market data
        </span>
        <span className="text-[12px] text-[var(--ink-4)]">
          Ads will go live within 1 hour of approval
        </span>
      </div>
    </div>
  );
}
